/**
 * Shortest decimal forms of f64 and f32 numbers for the JSON writers (W1-07a formats-json-rpe).
 *
 *  - f64: ECMAScript `Number.prototype.toString` already yields the shortest decimal that round-trips to the same
 *    double (exponent form below 1e-6 and from 1e21 on, both valid JSON).
 *  - f32: the shortest decimal d with `Math.fround(Number(d)) === Math.fround(v)`. That is exactly how serde_json
 *    reads an f32 field (decimal -> correctly rounded f64 -> `as f32`), so Phira reads back the same f32 the
 *    editor evaluates. Candidates with p = 1..9 significant digits are tried (9 always suffices); at each p the
 *    p-digit decimals around v are checked because the f32 rounding interval is asymmetric at powers of two.
 *
 * Both functions refuse non-finite values (the writers turn that into an EncodeError) and write -0 as 0.
 * `floatLexeme` adds the RPE float style: an integral float gets ".0" ("255.0"), as RPE writes floats.
 */

/** Shortest round-trip decimal of a finite f64 (-0 -> "0"). */
export function shortestF64(v: number): string {
  if (!Number.isFinite(v)) throw new RangeError(`shortestF64: not a finite number (${String(v)})`);
  if (v === 0) return '0';
  return String(v);
}

/** Splits `x.toExponential(p - 1)` into its p-digit integer mantissa and decimal exponent of the last digit. */
function mantissaOf(x: number, p: number): { readonly m: bigint; readonly e: number } {
  const s = Math.abs(x).toExponential(p - 1);
  const ePos = s.indexOf('e');
  const digits = s.slice(0, ePos).replace('.', '');
  const exp = Number(s.slice(ePos + 1));
  return { m: BigInt(digits), e: exp - (p - 1) };
}

/**
 * Shortest decimal that reads back (decimal -> f64 -> f32) as `Math.fround(v)`. -0 -> "0". The value is first
 * rounded to f32, so values outside the f32 range overflow to Infinity and are refused.
 */
export function shortestF32(v: number): string {
  const f = Math.fround(v);
  if (!Number.isFinite(f)) throw new RangeError(`shortestF32: not a finite f32 (${String(v)})`);
  if (f === 0) return '0';
  const neg = f < 0;
  const a = Math.abs(f);
  for (let p = 1; p <= 9; p++) {
    const { m, e } = mantissaOf(a, p);
    let best: number | null = null;
    for (const cand of [m - 1n, m, m + 1n]) {
      if (cand <= 0n) continue;
      const x = Number(`${cand.toString()}e${String(e)}`);
      if (Math.fround(x) !== a) continue;
      if (best === null || Math.abs(x - a) < Math.abs(best - a)) best = x;
    }
    if (best !== null) return neg ? `-${String(best)}` : String(best);
  }
  // Unreachable for finite f32 (9 significant digits always round-trip); keep the exact double as a fallback.
  return String(f);
}

/** True when a lexeme already reads as a float (has '.', 'e' or 'E'). */
export function hasFloatMark(lexeme: string): boolean {
  for (let i = 0; i < lexeme.length; i++) {
    const c = lexeme.charCodeAt(i);
    if (c === 0x2e || c === 0x65 || c === 0x45) return true;
  }
  return false;
}

/**
 * JSON lexeme of a float field: shortest f32 or f64 form, plus ".0" for integral values in the 'rpe' style.
 * Throws RangeError for non-finite values.
 */
export function floatLexeme(v: number, kind: 'f32' | 'f64', style: 'rpe' | 'shortest'): string {
  const s = kind === 'f32' ? shortestF32(v) : shortestF64(v);
  return style === 'rpe' && !hasFloatMark(s) ? `${s}.0` : s;
}

/** RFC 8259 number grammar. */
export const JSON_NUMBER_RE = /^-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?$/;
